import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import SetupAdmin from "./pages/SetupAdmin";
import Dashboard from "./pages/Dashboard";
import Cotizaciones from "./pages/Cotizaciones";
import Contratos from "./pages/Contratos";
import ContratoDetalle from "./pages/ContratoDetalle";
import NuevoContrato from "./pages/NuevoContrato";
import ContratosMarco from "./pages/ContratosMarco";
import ContratoMarcoEditor from "./pages/ContratoMarcoEditor";
import Calendario from "./pages/Calendario";
import Asignacion from "./pages/Asignacion";
import Logistica from "./pages/Logistica";
import Disponibilidad from "./pages/Disponibilidad";
import Unidades from "./pages/Unidades";
import Choferes from "./pages/Choferes";
import Mantenimiento from "./pages/Mantenimiento";
import Finanzas from "./pages/Finanzas";
import Comisiones from "./pages/Comisiones";
import ControlFinanciero from "./pages/ControlFinanciero";
import Configuracion from "./pages/Configuracion";
import Usuarios from "./pages/Usuarios";
import AlertConfig from "./pages/AlertConfig";
import SalesAgents from "./pages/SalesAgents";
import PagoSuccess from "./pages/PagoSuccess";
import Exportar from "./pages/Exportar";
import AdminReasignaciones from "./pages/AdminReasignaciones";
import CRMClientes from "./pages/CRMClientes";
import "@/App.css";

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/setup" element={<SetupAdmin />} />
      <Route path="/pagos/success" element={<PagoSuccess />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      {/* Redirect old /solicitudes to /cotizaciones */}
      <Route path="/solicitudes" element={<Navigate to="/cotizaciones" replace />} />
      
      <Route path="/cotizaciones" element={
        <ProtectedRoute>
          <Cotizaciones />
        </ProtectedRoute>
      } />
      
      <Route path="/contratos" element={
        <ProtectedRoute>
          <Contratos />
        </ProtectedRoute>
      } />
      
      <Route path="/contratos/:id" element={
        <ProtectedRoute>
          <ContratoDetalle />
        </ProtectedRoute>
      } />
      
      <Route path="/contratos/nuevo" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor", "vendedor", "vendedor_logistica", "logistica"]}>
          <NuevoContrato />
        </ProtectedRoute>
      } />
      
      <Route path="/contratos-marco" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor", "vendedor", "vendedor_logistica"]}>
          <ContratosMarco />
        </ProtectedRoute>
      } />
      
      <Route path="/contratos-marco/nuevo" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor", "vendedor", "vendedor_logistica"]}>
          <ContratoMarcoEditor />
        </ProtectedRoute>
      } />
      
      <Route path="/contratos-marco/:id" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor", "vendedor", "vendedor_logistica"]}>
          <ContratoMarcoEditor />
        </ProtectedRoute>
      } />
      
      <Route path="/calendario" element={
        <ProtectedRoute>
          <Calendario />
        </ProtectedRoute>
      } />

      <Route path="/logistica" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor", "logistica", "vendedor_logistica", "vendedor"]}>
          <Logistica />
        </ProtectedRoute>
      } />
      
      <Route path="/asignacion" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor", "logistica", "vendedor_logistica", "vendedor"]}>
          <Asignacion />
        </ProtectedRoute>
      } />
      
      <Route path="/disponibilidad" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor", "logistica", "vendedor_logistica", "vendedor"]}>
          <Disponibilidad />
        </ProtectedRoute>
      } />
      
      <Route path="/unidades" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor", "logistica", "mantenimiento", "vendedor_logistica"]}>
          <Unidades />
        </ProtectedRoute>
      } />
      
      <Route path="/choferes" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor", "logistica", "vendedor_logistica"]}>
          <Choferes />
        </ProtectedRoute>
      } />
      
      <Route path="/mantenimiento" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor", "logistica", "mantenimiento", "vendedor_logistica"]}>
          <Mantenimiento />
        </ProtectedRoute>
      } />
      
      <Route path="/finanzas" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor"]}>
          <Finanzas />
        </ProtectedRoute>
      } />
      
      <Route path="/control-financiero" element={
        <ProtectedRoute allowedRoles={["admin", "logistica", "vendedor_logistica"]}>
          <ControlFinanciero />
        </ProtectedRoute>
      } />
      
      <Route path="/comisiones" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor", "vendedor", "vendedor_logistica"]}>
          <Comisiones />
        </ProtectedRoute>
      } />
      
      <Route path="/usuarios" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor"]}>
          <Usuarios />
        </ProtectedRoute>
      } />
      
      <Route path="/configuracion" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor"]}>
          <Configuracion />
        </ProtectedRoute>
      } />
      
      <Route path="/configuracion/alertas" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AlertConfig />
        </ProtectedRoute>
      } />
      
      <Route path="/configuracion/vendedores" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor"]}>
          <SalesAgents />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/reasignaciones" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor"]}>
          <AdminReasignaciones />
        </ProtectedRoute>
      } />
      
      <Route path="/exportar" element={
        <ProtectedRoute>
          <Exportar />
        </ProtectedRoute>
      } />
      
      {/* CRM Routes - Sin vendedor_logistica */}
      <Route path="/crm/clientes" element={
        <ProtectedRoute allowedRoles={["admin", "admin_vendedor", "vendedor"]}>
          <CRMClientes />
        </ProtectedRoute>
      } />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
